/* eslint-disable complexity */
/* eslint-disable max-lines-per-function */
import { FeatureRollout } from '@ms/yammer-data/dist/state/ecsFeatureRollouts/types';
import { Experiment } from '@ms/yammer-data/dist/state/types';
import { useCanCurrentUserAccessWebAdminSettings } from '@ms/yammer-hooks/dist/adminSettings';
import { useIsCampaignsEnabled, useIsCampaignsHubEnabled } from '@ms/yammer-hooks/dist/campaignRights';
import {
  useAndReportExperimentTreatment,
  useExperimentTreatmentWithoutReporting,
} from '@ms/yammer-hooks/dist/experiments';
import { useIsFeatureRolloutEnabled } from '@ms/yammer-hooks/dist/featureRollouts';
import { useIsNewGroupFilesEnabled } from '@ms/yammer-hooks/dist/groups';
import { useAreLeadershipManagementFlightsEnabled } from '@ms/yammer-hooks/dist/leaderSettings';
import {
  useCanUserAccessLeaderDirectory,
  useCanUserAccessLeadershipCorner,
} from '@ms/yammer-hooks/dist/leadershipCorner';
import { useCurrentSessionUser } from '@ms/yammer-hooks/dist/session';
import {
  LegacyCreateGroup,
  LegacyDiscoverGroups,
  LegacyGroupDelete,
  LegacyGroupFiles,
  LegacyGroupInfo,
  LegacyGroupInsights,
  LegacyGroupSettings,
  LegacyNetworkAdminSettings,
  LegacyUserSettings,
} from '@ms/yammer-web-components/dist/route/legacyRoutes';
import {
  searchPathFiles,
  searchPathGroups,
  searchPathInbox,
  searchPathThreads,
  searchPathTopics,
  searchPathUsers,
} from '@ms/yammer-web-support/dist/urls';
import React, { FC } from 'react';
import { Route, Switch } from 'react-router-dom';

import PageRoot from '../PageRoot';

import {
  AdminSettings,
  Campaign,
  CampaignRouter,
  CampaignsDashboard,
  CampaignsHub,
  DelegationSettings,
  DiscoverGroups,
  Feed,
  Group,
  Inbox,
  InsightsCampaign,
  InsightsCampaignRouter,
  InsightsLeaders,
  InsightsQnA,
  LeaderDashboard,
  LeaderSettings,
  LeadersDirectory,
  LeadershipCorner,
  LegacyRouteConverter,
  LegacyRouter,
  QuestionSearch,
  QuestionThread,
  Questions,
  Search,
  StorylineAdminAdvancedSettings,
  StorylineAdminSettings,
  StorylineHome,
  StorylineRouter,
  Thread,
  Topic,
  TopicNetworkQuestion,
  TopicNetworkQuestionBrowse,
  UsagePolicy,
  User,
  UserAchievements,
} from './loadableComponents';

export const ContentRoutes: FC = () => {
  const isNewGroupFilesEnabled = useIsNewGroupFilesEnabled();
  const canUserAccessLeadershipCorner = useCanUserAccessLeadershipCorner();
  const canAccessAdminPage = useCanCurrentUserAccessWebAdminSettings();
  const isStorylineHomeEnabled = useIsFeatureRolloutEnabled(FeatureRollout.StorylineHome);
  const isNetworkQuestionsEnabledInGA = useExperimentTreatmentWithoutReporting(Experiment.AnswersInVivaGA) === true;
  const areCampaignsEnabled = useIsCampaignsEnabled();
  const isStorylineAdminEnabled = useAndReportExperimentTreatment(Experiment.StorylineAdmin) === 'enabled';
  const isStorylineAdminAdvancedEnabled =
    useAndReportExperimentTreatment(Experiment.StorylineAdminAdvanced) === 'enabled';
  const { isNetworkAdmin } = useCurrentSessionUser();
  const shouldShowStorylineAdminAdvancedSettingsPage =
    isStorylineAdminEnabled && isNetworkAdmin && isStorylineAdminAdvancedEnabled;
  const areLeadershipManagementFlightsEnabled = useAreLeadershipManagementFlightsEnabled();
  const canUserAccessLeaderDirectory = useCanUserAccessLeaderDirectory();

  const leadershipCorner = canUserAccessLeadershipCorner ? (
    <Route exact={true} path="/leadership-corner" component={LeadershipCorner} />
  ) : null;

  const leaderDashboard = canUserAccessLeadershipCorner ? (
    <Route exact={true} path="/leader-dashboard" component={LeaderDashboard} />
  ) : null;

  const legacyGroupFilesPage = !isNewGroupFilesEnabled ? (
    <Route exact={true} path="/groups/:groupId/files" component={LegacyGroupFiles} />
  ) : null;

  const leadersDirectoryPages = canUserAccessLeaderDirectory
    ? [
        <Route key="Leaders" exact={true} path="/leaders" component={LeadersDirectory} />,
        <Route key="LeadersList" exact={true} path="/leaders/:subRoute" component={LeadersDirectory} />,
      ]
    : null;

  const storylineHome = isStorylineHomeEnabled ? (
    <Route exact={true} path="/storylines" component={StorylineHome} />
  ) : null;

  const campaignsPages = areCampaignsEnabled
    ? [
        <Route
          key="CampaignsDashboard"
          exact={true}
          path="/admin/campaigns-dashboard"
          component={CampaignsDashboard}
        />,
        <Route
          key="CampaignsDashboardSubRoute"
          exact={true}
          path="/admin/campaigns-dashboard/:campaignsDashboardSubRoute"
          component={CampaignsDashboard}
        />,
      ]
    : null;

  const campaignsHubPage = useIsCampaignsHubEnabled() ? (
    <Route key="CampaignsHub" exact={true} path="/campaigns-hub" component={CampaignsHub} />
  ) : null;

  const leaderSettingPages = areLeadershipManagementFlightsEnabled ? (
    <Route key="UserSettingsLeaders" path="/user-settings/leaders" component={LeaderSettings} />
  ) : null;

  const storylineAdminAdvancedSettingsPage = shouldShowStorylineAdminAdvancedSettingsPage ? (
    <Route exact={true} path="/admin/settings/storyline/advanced" component={StorylineAdminAdvancedSettings} />
  ) : null;

  const userAchievementsRoute = isNetworkQuestionsEnabledInGA ? (
    <Route exact={true} path="/achievements/:userId" component={UserAchievements} />
  ) : null;

  const adminSettingsPages = canAccessAdminPage
    ? [
        <Route exact={true} key="AdminSettings" path="/admin" component={AdminSettings} />,
        <Route exact={true} key="AdminSettingsSubRoute" path="/admin/:subRoute" component={AdminSettings} />,
      ]
    : null;

  return (
    <Switch>
      <Route exact={true} path="/feed" component={Feed} />
      {adminSettingsPages}
      {storylineHome}
      <Route exact={true} path="/inbox" component={Inbox} />
      <Route exact={true} path="/inbox/:inboxFeedSubRoute" component={Inbox} />
      {campaignsPages}
      {campaignsHubPage}
      <Route exact={true} path="/create-group" component={LegacyCreateGroup} />
      <Route exact={true} path="/groups/:groupId" component={Group} />
      <Route exact={true} path="/groups/:groupId/settings" component={LegacyGroupSettings} />
      <Route exact={true} path="/groups/:groupId/delete" component={LegacyGroupDelete} />
      <Route exact={true} path="/groups/:groupId/info" component={LegacyGroupInfo} />
      <Route exact={true} path="/groups/:groupId/insights" component={LegacyGroupInsights} />
      {legacyGroupFilesPage}
      <Route exact={true} path="/groups/:groupId/:groupFeedSubRoute" component={Group} />
      <Route exact={true} path="/campaigns/:campaignId" component={Campaign} />
      <Route exact={true} path="/threads/:threadId/:topLevelMessageId?/:secondLevelMessageId?" component={Thread} />
      <Route exact={true} path="/users/:userId" component={User} />
      {userAchievementsRoute}
      <Route exact={true} path="/users/:userId/:userFeedSubRoute" component={User} />
      <Route exact={true} path="/topics/:topicId" component={Topic} />
      <Route exact={true} path="/topics/:topicId/:topicFeedSubRoute" component={Topic} />
      <Route exact={true} path="/usage-policy" component={UsagePolicy} />
      <Route exact={true} path="/user-settings" component={LegacyUserSettings} />
      <Route exact={true} path="/user-settings/delegates" component={DelegationSettings} />
      <Route exact={true} path="/admin/settings/storyline" component={StorylineAdminSettings} />
      {storylineAdminAdvancedSettingsPage}
      {leaderSettingPages}
      <Route exact={true} path="/network-admin" component={LegacyNetworkAdminSettings} />
      <Route exact={true} path="/discover-groups" component={DiscoverGroups} />
      <Route exact={true} path="/discover-groups/:discoverGroupsSubRoute" component={DiscoverGroups} />
      <Route exact={true} path={searchPathThreads} component={Search} />
      <Route exact={true} path={searchPathGroups} component={Search} />
      <Route exact={true} path={searchPathUsers} component={Search} />
      <Route exact={true} path={searchPathFiles} component={Search} />
      <Route exact={true} path={searchPathTopics} component={Search} />
      <Route exact={true} path={searchPathInbox} component={Search} />
      {leadershipCorner}
      {leaderDashboard}
      {leadersDirectoryPages}
      <Route key="QuestionsSearch" exact={true} path="/questions/search" component={QuestionSearch} />
      <Route
        key="Question"
        exact={true}
        path="/questions/:threadId/:topLevelMessageId?/:secondLevelMessageId?"
        component={QuestionThread}
      />
      <Route key="Questions" exact={true} path="/questions" component={Questions} />
      <Route
        key="TopicNetworkQuestionBrowse"
        exact={true}
        path="/topics-network-question/browse"
        component={TopicNetworkQuestionBrowse}
      />
      <Route
        key="TopicNetworkQuestionFeed"
        exact={true}
        path="/topics-network-question/:topicId"
        component={TopicNetworkQuestion}
      />
      <Route
        key="TopicNetworkQuestionFeedSubroute"
        exact={true}
        path="/topics-network-question/:topicId/:topicFeedSubRoute"
        component={TopicNetworkQuestion}
      />
      {adminSettingsPages}
      <Route exact={true} path="/insights/campaign" component={InsightsCampaignRouter} />
      <Route exact={true} path="/insights/campaign/:campaignId" component={InsightsCampaign} />
      <Route exact={true} path="/insights/leaders" component={InsightsLeaders} />
      <Route exact={true} path="/insights/leaders/:insightsLeadersSubroute" component={InsightsLeaders} />
      <Route exact={true} path="/insights/users/myInsights" component={InsightsLeaders} />
      <Route exact={true} path="/insights/qna" component={InsightsQnA} />
      <Route exact={true} path="/insights/qna/:insightsQnASubroute" component={InsightsQnA} />
      <Route exact={true} path="/:networkPermaLink/legacy/groups/:groupId/info" component={LegacyGroupInfo} />
      <Route exact={true} path="/:networkPermaLink/legacy/create-group" component={LegacyCreateGroup} />
      <Route exact={true} path="/:networkPermaLink/legacy/groups" component={LegacyDiscoverGroups} />
      <Route exact={true} path="/:networkPermaLink/users/:userId/storyline" component={StorylineRouter} />
      <Route exact={true} path="/:networkPermaLink/campaigns/:campaignId" component={CampaignRouter} />
      <Route exact={true} path="/:networkPermaLink/:entityType/:entityId/legacy/*" component={LegacyRouter} />
      <Route exact={true} path="/:networkPermaLink/legacy/:entityType/:entityId" component={LegacyRouter} />
      <Route exact={true} path="/:networkPermaLink/legacy/:entityType" component={LegacyRouter} />
      <Route exact={true} path="/:networkPermaLink/legacy/:entityType/show" component={LegacyRouter} />
      <Route exact={true} path="/:networkPermaLink/legacy/:entityType/inGroup" component={LegacyRouter} />
      <Route exact={true} path="/" component={PageRoot} />
      <Route exact={false} path="*" component={LegacyRouteConverter} />
    </Switch>
  );
};

export default ContentRoutes;
